import { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { clearAuthState, loadAuthState, saveAuthState } from '../lib/auth-store';
import { useTenantCurrent, useCurrentUser, useAuthSession } from '../lib/hooks';
import { getActiveMembership, isAgencyScopeRole } from '../lib/role-access';
import { Icon } from './ui';

const ROLE_LABELS: Record<string, string> = {
  SUPER_ADMIN: 'Platform admin',
  ADMIN: 'Administrator',
  AGENCY: 'Agency owner',
  AGENCY_DEMO: 'Agency (trial)',
  LOCATION_USER: 'Location member',
};

type NavEntry = {
  to: string;
  label: string;
  icon: ReactNode;
  section: 'workspace' | 'settings';
};

const NAV_ITEMS: NavEntry[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <Icon.Dashboard />, section: 'workspace' },
  { to: '/locations', label: 'Locations', icon: <Icon.Pin />, section: 'workspace' },
  { to: '/runs', label: 'Activity', icon: <Icon.Activity />, section: 'workspace' },
  { to: '/onboarding', label: 'Google connection', icon: <Icon.Plug />, section: 'settings' },
  { to: '/billing', label: 'Billing', icon: <Icon.Card />, section: 'settings' },
  { to: '/branding', label: 'Branding', icon: <Icon.Palette />, section: 'settings' },
  { to: '/users', label: 'Team', icon: <Icon.Users />, section: 'settings' },
  { to: '/admin', label: 'Platform admin', icon: <Icon.Shield />, section: 'settings' },
];

type SidebarProps = {
  mobileOpen?: boolean;
  onNavigate?: () => void;
};

export function Sidebar({ mobileOpen = false, onNavigate }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  const authState = loadAuthState();
  const { data: currentUser } = useCurrentUser();
  const { data: tenant } = useTenantCurrent();
  const { logoutMutation } = useAuthSession();

  const user = currentUser || authState.user;
  const memberships = user?.memberships || [];
  const activeMembership = getActiveMembership(user || null, authState.activeTenantId);
  const activeTenantId = activeMembership?.tenantId || memberships[0]?.tenantId || '';

  const isSuperAdmin = user?.globalRole === 'SUPER_ADMIN';
  const activeRole = activeMembership?.role || null;
  const locationUser = activeRole === 'LOCATION_USER';

  const filteredItems = NAV_ITEMS.filter((item) => {
    if (item.to === '/admin') {
      return isSuperAdmin;
    }

    if (locationUser) {
      return item.to === '/dashboard' || item.to === '/locations';
    }

    if (isAgencyScopeRole(activeRole) && item.to === '/admin') {
      return false;
    }

    return true;
  });

  const workspaceItems = filteredItems.filter((item) => item.section === 'workspace');
  const settingsItems = filteredItems.filter((item) => item.section === 'settings');

  const portalTitle =
    tenant?.branding?.portalTitle || activeMembership?.tenantName || 'Local Presence Studio';
  const roleLabel = isSuperAdmin
    ? ROLE_LABELS.SUPER_ADMIN
    : ROLE_LABELS[activeRole || ''] || 'Member';

  function renderNav(items: NavEntry[]) {
    return items.map((item) => {
      const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);
      return (
        <Link
          key={item.to}
          to={item.to}
          className={`nav-item ${active ? 'active' : ''}`}
          onClick={onNavigate}
        >
          {item.icon}
          {item.label}
        </Link>
      );
    });
  }

  return (
    <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
      <div className="sidebar-brand">
        {tenant?.branding?.logoUrl ? (
          <img src={tenant.branding.logoUrl} alt={`${portalTitle} logo`} className="sidebar-logo" />
        ) : (
          <div className="sidebar-logo-fallback" aria-hidden="true">
            {portalTitle
              .split(/\s+/)
              .slice(0, 2)
              .map((word: string) => word.charAt(0))
              .join('')
              .toUpperCase() || 'LP'}
          </div>
        )}
        <div>
          <h1>{portalTitle}</h1>
          <div className="subtitle">{roleLabel}</div>
        </div>
      </div>

      {memberships.length > 1 && (
        <div className="tenant-switcher">
          <label>
            <span>Workspace</span>
            <select
              value={activeTenantId}
              onChange={(event) => {
                const nextTenantId = event.target.value || null;
                const current = loadAuthState();
                saveAuthState({
                  ...current,
                  activeTenantId: nextTenantId,
                });
                window.location.reload();
              }}
            >
              {memberships.map((membership: any) => (
                <option key={membership.tenantId} value={membership.tenantId}>
                  {membership.tenantName}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <nav className="nav-list" aria-label="Workspace">
        {renderNav(workspaceItems)}
      </nav>

      {settingsItems.length > 0 && (
        <>
          <div className="nav-label">Settings</div>
          <nav className="nav-list" aria-label="Settings">
            {renderNav(settingsItems)}
          </nav>
        </>
      )}

      <div className="sidebar-footer">
        <button
          className="btn ghost"
          style={{ justifyContent: 'flex-start' }}
          onClick={async () => {
            try {
              await logoutMutation.mutateAsync();
            } catch {
              clearAuthState();
            }
            onNavigate?.();
            navigate('/login');
          }}
        >
          <Icon.Logout />
          Sign out
        </button>
      </div>
    </aside>
  );
}
